/**
 * Tracks the AI generation behind the summary on screen (id + template), via a
 * ref rather than state — recording it must never trigger a re-render.
 */

import { useCallback, useMemo, useRef } from 'react';

import type { ConversationSummaryEvent } from '@/lib/streamingClient';
import {
  NO_SUMMARY_GENERATION,
  regenTriggerHint,
  summaryGenerationFromEvent,
  summaryGenerationFromHttp,
} from '@/lib/summaryGeneration';
import type { SummaryGeneration, SummaryGenerationEcho } from '@/types/summaryGeneration';

export const useSummaryGeneration = () => {
  const ref = useRef<SummaryGeneration>(NO_SUMMARY_GENERATION);

  const recordFromEvent = useCallback((event: Pick<ConversationSummaryEvent, 'generation_id' | 'template_id'>) => {
    ref.current = summaryGenerationFromEvent(event);
  }, []);

  const recordFromHttp = useCallback((data: SummaryGenerationEcho, selectedTemplateId: number | null) => {
    ref.current = summaryGenerationFromHttp(data, selectedTemplateId);
  }, []);

  const reset = useCallback(() => {
    ref.current = NO_SUMMARY_GENERATION;
  }, []);

  const regenTrigger = useCallback(
    (selectedTemplateId: number, hasSummary: boolean) => regenTriggerHint(hasSummary, selectedTemplateId, ref.current),
    []
  );

  const generationId = useCallback(() => ref.current.generationId, []);

  return useMemo(
    () => ({ recordFromEvent, recordFromHttp, reset, regenTrigger, generationId }),
    [recordFromEvent, recordFromHttp, reset, regenTrigger, generationId]
  );
};
